import * as React from "react";

import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import Box from "@mui/material/Box";
import Rating, { IconContainerProps } from "@mui/material/Rating";
import Typography from "@mui/material/Typography";

const labels: Record<number, string> = {
    1: "Poor",
    3: "Fair",
    5: "Great",
};

function CustomStar({ value }: { value: number }) {
    return (
        <Box
            component="div"
            display="flex"
            flexDirection="column"
            alignItems="center"
        >
            <StarIcon fontSize="inherit" />
            <Typography variant="caption" color="initial">
                {labels[value]}
            </Typography>
        </Box>
    );
}

function CustomEmptyStar({ value }: { value: number }) {
    return (
        <Box
            component="div"
            display="flex"
            flexDirection="column"
            alignItems="center"
        >
            <StarBorderIcon fontSize="inherit" />
            <Typography variant="caption" color="initial">
                {labels[value]}
            </Typography>
        </Box>
    );
}

type ContainerProps = IconContainerProps & {
    rating: number | null;
};

function IconContainer(props: ContainerProps) {
    const { value, rating, ...other } = props;

    const shouldDisplayFilledStar = React.useMemo(() => {
        if (rating !== null) {
            return value <= rating;
        }
        return false;
    }, [rating, value]);

    return (
        <span role="button" tabIndex={-1}>
            {shouldDisplayFilledStar ? (
                <CustomStar value={value} {...other} />
            ) : (
                <CustomEmptyStar value={value} {...other} />
            )}
        </span>
    );
}

interface FiveStarRatingProps {
    value: number | null;
    onChange: (event: React.SyntheticEvent, newValue: number | null) => void;
    name?: string;
    sx?: object;
}

export default function FiveStarRating({
    value,
    onChange,
    name = "custom-rating",
    sx = { fontSize: "3rem" },
}: FiveStarRatingProps) {
    return (
        <Rating
            name={name}
            value={value}
            onChange={onChange}
            max={5}
            slotProps={{
                icon: {
                    component: (props) => (
                        <IconContainer {...props} rating={value} />
                    ),
                },
            }}
            sx={sx}
        />
    );
}
